import type { QueryResult, ResultValue } from '../lib/api';

/**
 * Insight panel (§6c, optional narrative). Produces a short, deterministic
 * summary of a result — largest group and its share, or trend endpoints — from
 * the numbers themselves. No LLM: the narrative is computed so it is always
 * faithful to the data. An AI narrative can later augment this behind the same
 * slot, clearly labelled per §6.3.
 */
export function InsightPanel({ result }: { result: QueryResult }): React.JSX.Element | null {
  const text = summarize(result);
  if (!text) return null;
  return (
    <p style={{ margin: '12px 0 0', color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.5 }}>
      <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Insight · </span>
      {text}
    </p>
  );
}

function summarize(result: QueryResult): string | null {
  const metricCol = result.columns.find((c) => c.kind === 'metric');
  const labelCol = result.columns.find((c) => c.kind === 'dimension' || c.kind === 'time');
  if (!metricCol) return null;
  const rows = result.rows;
  if (rows.length === 0) return 'No rows matched this query.';

  const num = (r: Record<string, ResultValue>): number => Number(r[metricCol.key]) || 0;

  if (!labelCol) {
    return `${metricCol.label} is ${rows[0]?.[metricCol.key] ?? '—'}.`;
  }

  if (labelCol.kind === 'time') {
    const first = rows[0]!;
    const last = rows[rows.length - 1]!;
    const change = num(last) - num(first);
    const dir = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
    return `Across ${rows.length} periods, ${metricCol.label.toLowerCase()} went ${dir} from ${num(first)} (${first[labelCol.key]}) to ${num(last)} (${last[labelCol.key]}).`;
  }

  const total = rows.reduce((s, r) => s + num(r), 0);
  const top = [...rows].sort((a, b) => num(b) - num(a))[0]!;
  const share = total > 0 ? Math.round((num(top) / total) * 100) : 0;
  return `${top[labelCol.key]} leads with ${num(top)} (${share}% of ${total}) across ${rows.length} groups.`;
}
